import React, { Component } from 'react';
import styled from 'styled-components';
import get from 'lodash/get';
import max from 'lodash/max';
import min from 'lodash/min';
import flatten from 'lodash/flatten';
import { I18n } from 'react-redux-i18n';
import moment from 'moment';

import Card from 'components/Card';
import CardHeader from 'components/CardHeader';
import Pills from 'components/Pills';
import ExportButton from 'components/Export';
import LineGraph from 'components/Charts/LineGraph';
import Select from 'components/Select';
import EvolutionIcon from 'components/EvolutionIcon';
import ChartInputsBloc from 'modules/Distribution/components/ChartInputsBloc';

const Container = styled.div`
  .pillsContainer {
    text-transform: initial !important;
  }

  .card {
    height: calc(100% - 60px);
  }

  .row {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .row > *:not(:last-child) {
    margin-right: 15px;
  }

  .recharts-cartesian-axis-tick {
    font-size: 12px;
    text {
      fill: rgb(102, 102, 102);
    }
  }

  .exportButtonContainer {
    padding-bottom: 10px;
    margin-top: 10px;
  }

  .rightComponentsContainer {
    display: flex;
    flex-direction: row;
    align-items: center;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    > :not(:last-child) {
      margin-right: 15px;
    }
  }

  .selectSetsContainer {
    width: 80%;
    margin: 2% auto;
  }

  .setListContainer {
    display: flex;
    flex-direction: row;
    align-items: center;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    margin-top: 5px;

    > div {
      margin: 0 !important;
      height: 30px !important;
      white-space: nowrap !important;
      width: initial !important;
    }
    .tag {
      width: initial !important;
    }
    > div:not(:last-child) {
      margin-right: 10px !important;
    }
  }

  .rhcTooltip {
    padding: 12px;
    min-width: 250px;
  }

  .rhcDate {
    color: #abb5be;
    text-transform: uppercase;
    margin-bottom: 10px;
  }

  .rchLine {
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
  }

  .rchLabel {
    font-size: 12px;
    line-height: 18px;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
  }

  .rchValue {
    font-size: 12px;
    line-height: 18px;
    margin-left: auto;
    font-family: ${(props) => props.theme.fonts.sfcompact_semibold};
  }

  .rhcTooltipContainer {
    min-width: 350px;
    display: flex;
    flex-direction: row;
    align-items: center;
    font-family: ${(props) => props.theme.fonts.sfcompact};
    font-size: 12px;
    width: 100%;
    margin-bottom: 10px;
    > p {
      color: #abb5be;
      font-family: ${(props) => props.theme.fonts.sfcompact_text};
      font-weight: 600;
    }
  }

  .rhcTooltipValues {
    margin-left: auto;
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 75%;
    p {
      margin-left: 5px;
    }
  }

  .rhcTooltipValues > * {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 33%;
    text-align: center;
  }

  .rhcTooltipValues > div:not(:last-child) {
    margin-right: 15px;
  }

  .rhcTooltip {
    padding: 12px;
    min-width: 250px;
  }

  .rhcDate {
    color: #abb5be;
    text-transform: uppercase;
  }

  .rhcLabel {
    text-transform: uppercase;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    font-weight: 600;
  }

  .cardHeaderRight {
    width: 100%;
    justify-content: flex-end;
  }

  @media (max-width: 1600px) {
    > *:first-child > *:first-child {
      flex-direction: column !important;
      align-items: flex-start;
    }

    .exportButton {
      position: absolute;
      top: -5px;
      right: 0;
    }

    .cardHeaderRight {
      justify-content: flex-start;
      align-items: center;
    }

    .row > *:not(:last-child) {
      margin-top: 2px;
    }
  }

  .yAxis .recharts-cartesian-axis-tick:first-child {
    display: block !important;
  }
`;

const Tooltip = ({ data = [], index, markets = [] }) => {
  return (
    <div className="rhcTooltip">
      <div className="rhcTooltipContainer">
        <div className="rhcTooltipValues">
          <p className="rhcLabel">{I18n.t('lowPrice')}</p>
          <p className="rhcLabel">{I18n.t('averagePrice')}</p>
          <p className="rhcLabel">{I18n.t('highPrice')}</p>
        </div>
      </div>
      {data
        .filter((set) => set[index] && set[index].value)
        .map((set) => set[index])
        .map((set, indexSet) => (
          <React.Fragment
            key={[set.name, set.wine_name, index, indexSet].join('-')}
          >
            <div className="rhcTooltipContainer">
              <p>{`${set.wine_name} ${set.name}`}</p>
              <div className="rhcTooltipValues">
                {Object.keys(set)
                  .filter((key) => set[key] && set[key].value)
                  .map((key) => (
                    <div
                      key={[set.name, set.wine_name, index, indexSet, key].join(
                        '-'
                      )}
                    >
                      <EvolutionIcon icon={set[key].icon} />
                      <p>
                        {set[key].value.toLocaleString('fr-FR', {
                          style: 'currency',
                          currency: markets
                            ? get(set, 'displayed_currency', 'EUR')
                            : get(set, 'local_currency', 'EUR'),
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 2
                        })}
                      </p>
                    </div>
                  ))}
              </div>
            </div>
            <div className="rhcTooltipContainer">
              <div className="rhcTooltipValues">
                <p>{set.min_store}</p>
                <p />
                <p>{set.max_store}</p>
              </div>
            </div>
          </React.Fragment>
        ))}
    </div>
  );
};

class OnlineSalesLineChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tagList: [],
      activePill: 'avg_price'
    };
  }

  averageData(data) {
    const averagedData = {};

    data.forEach((item) => {
      if (averagedData[item.date]) {
        averagedData[item.date].sumFacing += item.facing;
        if (item.min_price < averagedData[item.date].min_price) {
          averagedData[item.date].min_price = item.min_price;
          averagedData[item.date].min_store = item.min_store;
        }
        if (item.max_price > averagedData[item.date].max_price) {
          averagedData[item.date].max_price = item.max_price;
          averagedData[item.date].max_store = item.max_store;
        }
        if (item.min_local_price < averagedData[item.date].min_local_price) {
          averagedData[item.date].min_local_price = item.min_local_price;
        }
        if (item.max_local_price > averagedData[item.date].max_local_price) {
          averagedData[item.date].max_local_price = item.max_local_price;
        }

        Object.keys(averagedData[item.date]).forEach((key) => {
          if (key === 'avg_price' || key === 'avg_local_price') {
            averagedData[item.date][key] =
              (averagedData[item.date][key] *
                (averagedData[item.date].sumFacing - item.facing) +
                item[key] * item.facing) /
              averagedData[item.date].sumFacing;
          }
        });
      } else {
        averagedData[item.date] = {
          ...item,
          avg_price: item.avg_price,
          sumFacing: item.facing
        };
      }
    });

    return Object.values(averagedData);
  }

  formatData() {
    const {
      data = [],
      dataClient = [],
      rivalSelection,
      allMarket
    } = this.props;

    const { activePill } = this.state;

    let formattedData = allMarket
      ? this.averageData([...dataClient])
      : [...dataClient];

    formattedData.sort(
      (a, b) =>
        moment(a.date).format('YYYYMMDD') - moment(b.date).format('YYYYMMDD')
    );
    formattedData = [formattedData];

    if (rivalSelection && rivalSelection.length > 0) {
      const rivals = rivalSelection.map((item) => item.label);
      rivals.forEach((rival) => {
        const rivalData = allMarket
          ? this.averageData(data.filter((item) => item.wine_name === rival))
          : data.filter((item) => item.wine_name === rival);

        formattedData.push(
          rivalData.sort(
            (a, b) =>
              moment(a.date).format('YYYYMMDD') -
              moment(b.date).format('YYYYMMDD')
          )
        );
      });
    }

    formattedData = formattedData.map((set) =>
      set
        .filter((item) => (allMarket ? item.avg_price : item.avg_local_price))
        .map((item) =>
          activePill === 'avg_price'
            ? {
                ...item,
                name: moment(item.date).format('DD/MM/YYYY'),
                minPrice: {
                  icon: 'down',
                  value: allMarket ? item.min_price : item.min_local_price
                },
                value: {
                  icon: 'neutral',
                  value: allMarket ? item.avg_price : item.avg_local_price
                },
                maxPrice: {
                  icon: 'up',
                  value: allMarket ? item.max_price : item.max_local_price
                }
              }
            : {
                ...item,
                name: moment(item.date).format('DD/MM/YYYY'),
                value: {
                  icon: 'down',
                  value: allMarket ? item.min_price : item.min_local_price
                },
                avgPrice: {
                  icon: 'neutral',
                  value: allMarket ? item.avg_price : item.avg_local_price
                },
                maxPrice: {
                  icon: 'up',
                  value: allMarket ? item.max_price : item.max_local_price
                }
              }
        )
    );
    let setNames = [];
    let wines = [];
    formattedData.forEach((set) => {
      if (set && set.length > 0) {
        set.sort(
          (a, b) =>
            Number(moment(a.name, 'DD/MM/YYYY').format('YYYYMMDD')) -
            Number(moment(b.name, 'DD/MM/YYYY').format('YYYYMMDD'))
        );
        setNames.push(...set.map((item) => item.name));
        wines.push(get(set, '[0].wine_name'));
      }
    });

    setNames = [...new Set(setNames)].sort(
      (a, b) =>
        Number(moment(a, 'DD/MM/YYYY').format('YYYYMMDD')) -
        Number(moment(b, 'DD/MM/YYYY').format('YYYYMMDD'))
    );

    formattedData.forEach((set, setIndex) => {
      setNames.forEach((name, index) => {
        if (!set[index] || set[index].name !== name) {
          set.splice(index, 0, {
            name,
            wine_name: wines[setIndex]
          });
        }
      });
    });
    return formattedData;
  }

  getLabels(data) {
    if (data && data.length > 0) {
      return data.map((set) => (set && set.length > 0 ? set[0].wine_name : ''));
    }
    return [];
  }

  getDataMax(data) {
    return max(
      flatten(
        [...data].map((set) => set.map((item) => parseInt(item.avg_price, 10)))
      )
    );
  }

  getDataMin(data) {
    return min(
      flatten(
        [...data].map((set) => set.map((item) => parseInt(item.avg_price, 10)))
      )
    );
  }

  render() {
    const {
      title,
      premium,
      exportOptions,
      vintage,
      handleVintage,
      vintageOptions,
      allMarket,
      shouldReduceSize,
      rivalList,
      rivalSelection,
      handleRivalSelection
    } = this.props;
    const { activePill } = this.state;
    const formattedData = this.formatData();
    const maxData = this.getDataMax(formattedData);
    const minData = this.getDataMin(formattedData);
    const labels = this.getLabels(formattedData);

    return (
      <Container>
        <Card className="card" id="vente-en-ligne-evolution-prix">
          <CardHeader
            alignItems="center"
            title={
              <div className="row">
                <p>{title}</p>
                <div className="pillsContainer ignored-export">
                  <Pills
                    options={[
                      { label: I18n.t('average'), value: 'avg_price' },
                      { label: I18n.t('low'), value: 'low_price' }
                    ].map((item) => ({
                      ...item,
                      active: activePill === item.value
                    }))}
                    onChange={({ value }) =>
                      this.setState({ activePill: value })
                    }
                  />
                </div>
              </div>
            }
            styles={`
              flex-direction:row;
              align-items:center;
            `}
            fontSize="18px"
            iconRight="right"
            rightComponents={
              <div className="row ignored-export">
                <ChartInputsBloc
                  vintageStart={vintage}
                  noVintageEnd={true}
                  vintageOptions={vintageOptions}
                  onChange={(field, value) => handleVintage(value)}
                />
                <div className="exportButton">
                  <ExportButton
                    name={`${title}-${
                      get(exportOptions, 'market')
                        ? get(exportOptions, 'market')
                        : I18n.t('placeholderAll')
                    }-${get(exportOptions, 'date')}-${get(
                      exportOptions,
                      'wine.label'
                    )}`}
                    jpg
                    pdf
                    targetElement="#vente-en-ligne-evolution-prix"
                  />
                </div>
              </div>
            }
          />
          <LineGraph
            height={shouldReduceSize ? 340 : 375}
            data={formattedData}
            labels={labels}
            onlyCircle
            customColors={[
              '#e25d34',
              'rgba(43, 61, 84,1)',
              'rgba(43, 61, 84,0.35)',
              'rgba(43, 61, 84,0.85)',
              'rgba(43, 61, 84,0.2)',
              'rgba(43, 61, 84,0.7)'
            ]}
            tooltipContent={(data, index) => (
              <Tooltip data={data} index={index} markets={allMarket} />
            )}
            yAxisProps={{
              scale: 'linear',
              tickCount: 6,
              type: 'number',
              height: 4000,
              interval: 'preserveStart',
              tickFormatter: (value) =>
                value.toLocaleString('fr-FR', {
                  style: 'currency',
                  currency: allMarket
                    ? get(formattedData, '[0][0].displayed_currency', 'EUR')
                    : get(formattedData, '[0][0].local_currency', 'EUR'),
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                }),
              domain: [
                () => (minData ? Number(minData - minData / 2).toFixed(0) : 0),
                () =>
                  maxData ? Number(maxData + maxData / 3 + 5).toFixed(0) : 0
              ]
            }}
            tooltipOffset={{ x: -110, y: -20 * formattedData.length }}
            tooltipOffsetLeft={{ x: -100, y: -30 + -20 * formattedData.length }}
            tooltipOffsetRight={{
              x: -150,
              y: 35 + -20 * formattedData.length
            }}
            strokeDasharray={[null, ['5 5'], ['5 5']]}
          />
          {premium && (
            <div className="selectSetsContainer ignored-export">
              <Select
                placeholder={I18n.t('placeholderRivals')}
                minWidth="170px"
                isMulti
                value={rivalSelection}
                dropdownProps={{
                  styles: `margin-left:20px;margin-right:20px;`
                }}
                onChange={(value) => handleRivalSelection(value)}
                options={rivalList.map((item) => ({
                  value: item,
                  label: item
                }))}
              />
            </div>
          )}
        </Card>
      </Container>
    );
  }
}

export default OnlineSalesLineChart;
